import React from "react";
import { DETAIL_ACCENTS } from "./DetailUI";

// Shared look for the full-page Add/Edit forms (AddFlight, AddInventory, AddSupplier, AddAgent, AddVendor).
// Pages alias these as labelStyle / inputStyle / readOnlyStyle / selectStyles / sectionCard / sectionTitle / field.
// Focus rings, hover and placeholder states live in App.css under ".v2a-form" (put that class on the <form>).

export const FORM_GRAD = "linear-gradient(135deg, var(--btn-bg) 0%, var(--btn-hover-bg) 100%)";

const CARD_PAD = 22;
const BORDER = "#e2e8f0";
const FIELD_BG = "#fbfcfe";
const RING = "0 0 0 4px rgba(14, 106, 150, .12)";

export const formLabelStyle = { display: "block", fontWeight: 600, fontSize: 12.5, color: "#334155", marginBottom: 6 };

export const formInputStyle = {
  borderRadius: 10, border: `1.5px solid ${BORDER}`, fontSize: 13.5, height: 42, background: FIELD_BG, color: "#0f172a",
  transition: "border-color .15s, box-shadow .15s, background-color .15s",
};

export const formReadOnlyStyle = {
  ...formInputStyle, border: "1.5px dashed #cbd5e1", background: "#f1f5f9", color: "#475569", cursor: "not-allowed",
};

export const formSelectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 42,
    borderRadius: 10,
    fontSize: 13.5,
    border: state.isDisabled
      ? "1.5px dashed #cbd5e1"
      : `1.5px solid ${state.isFocused ? "var(--btn-bg)" : BORDER}`,
    background: state.isDisabled ? "#f1f5f9" : state.isFocused ? "#fff" : FIELD_BG,
    boxShadow: state.isFocused ? RING : "none",
    transition: "border-color .15s, box-shadow .15s, background-color .15s",
    "&:hover": { borderColor: state.isFocused ? "var(--btn-bg)" : "#cbd5e1" },
  }),
  valueContainer: (base) => ({ ...base, padding: "0 12px" }),
  placeholder: (base) => ({ ...base, color: "#94a3b8", fontSize: 13.5 }),
  singleValue: (base, state) => ({ ...base, color: state.isDisabled ? "#64748b" : "#0f172a", fontWeight: 500 }),
  indicatorSeparator: () => ({ display: "none" }),
  dropdownIndicator: (base, state) => ({
    ...base,
    color: state.isFocused ? "var(--btn-bg)" : "#94a3b8",
    transform: state.selectProps.menuIsOpen ? "rotate(180deg)" : "none",
    transition: "transform .2s, color .15s",
    "&:hover": { color: "var(--btn-bg)" },
  }),
  clearIndicator: (base) => ({ ...base, color: "#cbd5e1", "&:hover": { color: "#ef4444" } }),
  menu: (base) => ({
    ...base, borderRadius: 12, overflow: "hidden", border: "1px solid #eef1f7",
    boxShadow: "0 12px 32px rgba(15, 23, 42, .14)", marginTop: 6,
  }),
  menuList: (base) => ({ ...base, padding: 6 }),
  option: (base, state) => ({
    ...base,
    fontSize: 13.5,
    borderRadius: 8,
    cursor: "pointer",
    background: state.isSelected ? "var(--btn-bg)" : state.isFocused ? "color-mix(in srgb, var(--btn-bg) 9%, #fff)" : "transparent",
    color: state.isSelected ? "#fff" : "#1e293b",
    fontWeight: state.isSelected ? 600 : 400,
    "&:active": { background: "color-mix(in srgb, var(--btn-bg) 18%, #fff)" },
  }),
  multiValue: (base) => ({ ...base, borderRadius: 6, background: "color-mix(in srgb, var(--btn-bg) 12%, #fff)" }),
  multiValueLabel: (base) => ({ ...base, color: "var(--btn-hover-bg)", fontWeight: 600 }),
  noOptionsMessage: (base) => ({ ...base, fontSize: 13, color: "#94a3b8" }),
  menuPortal: (base) => ({ ...base, zIndex: 2000 }),
};

export const formSectionCard = {
  background: "#fff", borderRadius: 16, border: "1px solid #e9edf5", padding: CARD_PAD, marginBottom: 18,
  boxShadow: "0 1px 2px rgba(15, 23, 42, .04), 0 6px 18px rgba(15, 23, 42, .04)",
};

// section colour picked from its title, so each block of a form is visually distinct
const SECTION_ACCENTS = [
  [/stop|segment|via/, DETAIL_ACCENTS.violet],
  [/arrival|destination/, DETAIL_ACCENTS.teal],
  [/departure|origin|sector/, DETAIL_ACCENTS.indigo],
  [/airline|flight/, DETAIL_ACCENTS.sky],
  [/schedule|time|date|terminal/, DETAIL_ACCENTS.amber],
  [/fare|price|pnr|seat|amount|wallet|payment|markup|commission/, DETAIL_ACCENTS.green],
  [/login|password|credential|security|account/, DETAIL_ACCENTS.pink],
  [/document|upload|file|kyc/, DETAIL_ACCENTS.violet],
  [/contact|person|executive/, DETAIL_ACCENTS.sky],
  [/address|location/, DETAIL_ACCENTS.teal],
  [/company|agency|supplier|vendor|agent|basic/, DETAIL_ACCENTS.indigo],
];

const accentFor = (text) => {
  const t = String(text || "").toLowerCase();
  const hit = SECTION_ACCENTS.find(([re]) => re.test(t));
  return hit ? hit[1] : DETAIL_ACCENTS.teal;
};

// Coloured header band at the top of a section card (sits flush against the card edges).
export const formSectionTitle = (icon, text, subtitle, accent = accentFor(text)) => (
  <div
    className="d-flex align-items-center gap-3"
    style={{
      margin: `-${CARD_PAD}px -${CARD_PAD}px 20px`,
      padding: `14px ${CARD_PAD}px`,
      borderRadius: "16px 16px 0 0",
      borderBottom: "1px solid #eef1f7",
      background: `linear-gradient(90deg, ${accent}17 0%, ${accent}05 55%, transparent 100%)`,
    }}
  >
    <span
      className="d-inline-flex align-items-center justify-content-center flex-shrink-0"
      style={{ width: 36, height: 36, borderRadius: 10, background: accent, color: "#fff", fontSize: 16, boxShadow: `0 4px 12px ${accent}55` }}
    >
      <i className={"bi " + icon}></i>
    </span>
    <div style={{ minWidth: 0 }}>
      <div className="fw-bold" style={{ fontSize: 15, color: "#0f172a", lineHeight: 1.25 }}>{text}</div>
      {subtitle && <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 2 }}>{subtitle}</div>}
    </div>
  </div>
);

// Inline result of an onChange availability check, shown under an input
export const formAvailabilityMsg = (taken, checking) =>
  taken ? (
    <div style={{ fontSize: 11.5, color: "#dc2626", marginTop: 4 }}>{taken}</div>
  ) : checking ? (
    <div style={{ fontSize: 11.5, color: "#94a3b8", marginTop: 4 }}>Checking availability...</div>
  ) : null;

export const formField =(label, required, node, cls = "col-12 col-md-6 col-lg-4") => (
  <div className={cls}>
    <label style={formLabelStyle}>
      {label}
      {required && <span style={{ color: "#ef4444", marginLeft: 3 }}>*</span>}
    </label>
    {node}
  </div>
);
